import { useCallback, useEffect } from 'react';
import { makeVar, ReactiveVar, useReactiveVar } from '@apollo/client';

import { useBasket } from '@hook/useBasket';

import { useProductSizesSelectQuery } from './gql/graphql.v3.generated';

let selectedSizeIdsVar: ReactiveVar<number[]> = makeVar<number[]>([]);

if (typeof window !== 'undefined') {
  if (!window.SELECTED_SIZES_VAR) {
    window.SELECTED_SIZES_VAR = makeVar<number[]>([]);
  }

  selectedSizeIdsVar = window.SELECTED_SIZES_VAR;
}

export const useProductSizeSelect = (productId: number) => {
  const selectedSizeIds = useReactiveVar(selectedSizeIdsVar);
  const { isInBasket, basket } = useBasket();
  const { data, loading } = useProductSizesSelectQuery({
    variables: { productId },
    context: { batch: true },
    skip: !productId,
  });


  const onSelectSize = useCallback(
    (sizeId: number) => {
      if (selectedSizeIds[0] === sizeId && !isInBasket({ productId, sizeId })) {
        selectedSizeIdsVar([]);
      } else {
        selectedSizeIdsVar([sizeId]);
      }
    },
    [selectedSizeIds, isInBasket, productId],
  );

  useEffect(
    () => {
      const sizes = data?.productDetails.sizes;

      if (!selectedSizeIds?.length) {
        if (sizes?.length === 1) {
          onSelectSize(sizes[0].id);
          return;
        }

        // Basket logic
        if (basket) {
          let lastAddedSize;
          for (const group of basket.groups) {
            for (const basketProduct of [...group.basketProducts].reverse()) {
              if (basketProduct.product?.id === productId) {
                lastAddedSize = basketProduct.size?.id;
              }
            }
          }

          if (lastAddedSize) {
            onSelectSize(lastAddedSize);
          }
        }
      }
    },
    [basket, selectedSizeIds, productId, onSelectSize, data?.productDetails.sizes],
  );

  return {
    sizes: data?.productDetails.sizes || [],
    selectedSizeIds,
    loading,
    onSelectSize,
  };
};
